/**
 * <video-stories>: the story player behind blocks/video-stories.liquid.
 *
 * Ported from Luttz (assets/component-video-stories.js) without its framework:
 * the viewer is a native <dialog> (focus, Esc and the backdrop come from the
 * platform) and the slides are stacked, only the active one visible. What this
 * adds is the sequence:
 *   - play the active video (muted unless sound was turned on) and stop the rest;
 *   - fill the active segment (`--stories-progress`, 0 to 1) from `timeupdate`;
 *   - advance when a video ends, close after the last one;
 *   - remember, per session, which stories were already seen (the ring fades).
 */
(() => {
  if (customElements.get('video-stories')) return;

  const SEEN_KEY = 'duorama:stories-seen';

  const readSeen = () => {
    try {
      return JSON.parse(sessionStorage.getItem(SEEN_KEY) ?? '[]');
    } catch {
      return [];
    }
  };

  const writeSeen = (seen) => {
    try {
      sessionStorage.setItem(SEEN_KEY, JSON.stringify(seen.slice(-120)));
    } catch {
      /* private tab or full storage: the ring just does not fade */
    }
  };

  class VideoStories extends HTMLElement {
    #index = -1;
    #controller = null;

    connectedCallback() {
      this.viewer = this.querySelector('dialog');
      if (!this.viewer) return;

      this.#controller = new AbortController();
      const signal = this.#controller.signal;

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.open(Number(trigger.dataset.story ?? 0)), { signal });
      });

      this.querySelector('[data-story-previous]')?.addEventListener('click', () => this.previous(), { signal });
      this.querySelector('[data-story-next]')?.addEventListener('click', () => this.next(), { signal });
      this.querySelector('[data-story-sound]')?.addEventListener('click', () => this.toggleSound(), { signal });
      this.querySelector('[data-story-close]')?.addEventListener('click', () => this.viewer.close(), { signal });

      // A click on the backdrop (outside the stage) closes the viewer.
      this.viewer.addEventListener(
        'click',
        (event) => {
          if (event.target === this.viewer) this.viewer.close();
        },
        { signal }
      );
      this.viewer.addEventListener('close', () => this.#onClose(), { signal });
      this.viewer.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowRight') this.next();
          else if (event.key === 'ArrowLeft') this.previous();
        },
        { signal }
      );

      this.#paintSeen();
    }

    disconnectedCallback() {
      this.#controller?.abort();
    }

    get triggers() {
      return [...this.querySelectorAll('[data-story]')];
    }

    get slides() {
      return [...this.querySelectorAll('[data-slide]')];
    }

    get videos() {
      return this.slides.map((slide) => slide.querySelector('video'));
    }

    get segments() {
      return [...this.querySelectorAll('[data-segment]')];
    }

    get soundOn() {
      return this.querySelector('[data-story-sound]')?.getAttribute('aria-pressed') === 'true';
    }

    open(index) {
      if (!this.viewer.open) {
        document.documentElement.style.overflow = 'hidden';
        this.viewer.showModal();
      }
      this.#activate(index);
    }

    next() {
      if (this.#index >= this.slides.length - 1) this.viewer.close();
      else this.#activate(this.#index + 1);
    }

    previous() {
      this.#activate(Math.max(0, this.#index - 1));
    }

    toggleSound() {
      const button = this.querySelector('[data-story-sound]');
      if (!button) return;

      const on = !this.soundOn;
      button.setAttribute('aria-pressed', String(on));
      button.setAttribute('aria-label', on ? 'Desativar som' : 'Ativar som');

      const current = this.videos[this.#index];
      if (current) current.muted = !on;
    }

    #activate(index) {
      const videos = this.videos;
      const current = videos[index];
      if (!current) return;

      this.#index = index;

      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));

      videos.forEach((video, i) => {
        if (!video || i === index) return;
        video.pause();
        video.currentTime = 0;
        video.removeEventListener('timeupdate', this.#onTimeUpdate);
        video.removeEventListener('ended', this.#onEnded);
      });

      this.segments.forEach((segment, i) => {
        segment.toggleAttribute('data-done', i < index);
        if (i !== index) segment.style.removeProperty('--stories-progress');
      });

      current.muted = !this.soundOn;
      current.addEventListener('timeupdate', this.#onTimeUpdate);
      current.addEventListener('ended', this.#onEnded);
      current.play().catch(() => {
        /* autoplay refused: the first frame stays until the customer taps */
      });

      this.#markSeen(index);
    }

    #onTimeUpdate = () => {
      const video = this.videos[this.#index];
      const segment = this.segments[this.#index];
      if (!video || !segment || !Number.isFinite(video.duration) || video.duration === 0) return;

      segment.style.setProperty('--stories-progress', String(video.currentTime / video.duration));
    };

    #onEnded = () => this.next();

    #onClose() {
      this.videos.forEach((video) => {
        if (!video) return;
        video.pause();
        video.currentTime = 0;
        video.removeEventListener('timeupdate', this.#onTimeUpdate);
        video.removeEventListener('ended', this.#onEnded);
      });
      this.slides.forEach((slide) => slide.classList.remove('is-active'));
      document.documentElement.style.removeProperty('overflow');
      this.triggers[this.#index]?.focus();
      this.#index = -1;
    }

    #seenId(index) {
      const id = this.triggers[index]?.dataset.storyId;
      return id ? `${this.dataset.productId}:${id}` : null;
    }

    #markSeen(index) {
      const id = this.#seenId(index);
      if (!id) return;

      const seen = readSeen();
      if (seen.includes(id)) return;

      seen.push(id);
      writeSeen(seen);
      this.#paintSeen();
    }

    #paintSeen() {
      const seen = readSeen();
      this.triggers.forEach((trigger, i) => {
        const id = this.#seenId(i);
        trigger.toggleAttribute('data-viewed', id != null && seen.includes(id));
      });
    }
  }

  customElements.define('video-stories', VideoStories);
})();
